// Variation register table. Seven columns per brief §5 layer 3b:
//   Name | Number | Varies | Effective | Value impact | Status | Actions
//
// Renders via the shared TrackingTable shell so the inline-expand
// pattern, keyboard semantics, and deep-link highlight ring all match
// the other tracking surfaces. Actions are context-sensitive: when
// status === 'proposed' the row exposes ✓ Approve and ✕ Reject buttons;
// terminal-status rows (approved / rejected) show only Edit + Archive.

import { CheckCircle2, XCircle, Pencil, Archive } from "lucide-react";
import TrackingTable, { type Column } from "../shared/TrackingTable";
import VariationDateCell from "./VariationDateCell";
import VariationStatusPill from "./VariationStatusPill";
import ValueImpactCell from "./ValueImpactCell";
import VariesCell from "./VariesCell";
import { readTargets } from "./variationTargets";
import CopyableIdentifier from "../../../common/CopyableIdentifier";
import type { Sow, Variation } from "../../../../types";
import type { ReactNode } from "react";

export type VariationAction =
  | "approve"
  | "reject"
  | "edit"
  | "archive";

export interface VariationTableProps {
  items: Variation[];
  isLoading: boolean;
  error: unknown;
  gstApplies: boolean | undefined;
  /** Resolved-SOW lookup for the polymorphic Varies cell. */
  sowsById: Map<string, Sow>;
  /** Imperative scroll-to + expand handler for the SOW deep-link. */
  onNavigateToSow?: (sowId: string) => void;
  onRowClick?: (item: Variation) => void;
  onAction?: (item: Variation, action: VariationAction) => void;
  highlightRowId?: string | null;
  expandedRowId?: string | null;
  renderExpandedRow?: (item: Variation) => ReactNode;
  emptyMessage?: string;
}

export default function VariationTable({
  items,
  isLoading,
  error,
  gstApplies,
  sowsById,
  onNavigateToSow,
  onRowClick,
  onAction,
  highlightRowId,
  expandedRowId,
  renderExpandedRow,
  emptyMessage = "No variations recorded yet.",
}: VariationTableProps) {
  const columns: Column<Variation>[] = [
    {
      header: "Name",
      width: "28%",
      clickable: true,
      cell: (item) => (
        <div className="flex flex-col">
          <span className="text-body text-slate-900 dark:text-slate-100 font-medium">
            {item.name}
          </span>
          {item.description && (
            <span className="text-caption text-slate-500 dark:text-slate-400 line-clamp-1">
              {item.description}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Number",
      width: "12%",
      cell: (item) => (
        <CopyableIdentifier
          value={item.number}
          label={`Variation number ${item.number}`}
        />
      ),
    },
    {
      header: "Varies",
      width: "18%",
      cell: (item) => (
        <VariesCell
          targets={readTargets(item)}
          sowsById={sowsById}
          onNavigate={onNavigateToSow}
          testId={`variation-row-${item.id}-varies`}
        />
      ),
    },
    {
      header: "Effective",
      width: "14%",
      cell: (item) => (
        <VariationDateCell
          effectiveDate={item.effectiveDate}
          status={item.status}
          approvedAt={item.approvedAt}
          rejectedAt={item.rejectedAt}
          proposedAt={item.createdAt}
        />
      ),
    },
    {
      header: "Value impact",
      width: "11%",
      align: "right",
      cellClassName: "tabular-nums",
      cell: (item) => (
        <ValueImpactCell
          value={item.valueImpact ?? null}
          gstApplies={gstApplies}
          testId={`variation-row-${item.id}-value`}
        />
      ),
    },
    {
      header: "Status",
      width: "11%",
      cell: (item) => <VariationStatusPill status={item.status} />,
    },
    {
      header: "Actions",
      width: "16%",
      align: "right",
      cell: (item) => {
        const isProposed = item.status === "proposed";
        return (
          <div className="inline-flex gap-1 justify-end">
            {isProposed && (
              <>
                <button
                  type="button"
                  aria-label={`Approve ${item.name}`}
                  data-testid={`variation-row-${item.id}-approve`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(item, "approve");
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Reject ${item.name}`}
                  data-testid={`variation-row-${item.id}-reject`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(item, "reject");
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  <XCircle className="h-4 w-4" aria-hidden="true" />
                </button>
              </>
            )}
            <button
              type="button"
              aria-label={`Edit ${item.name}`}
              data-testid={`variation-row-${item.id}-edit`}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(item, "edit");
              }}
              className="inline-flex items-center justify-center w-8 h-8 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={`Archive ${item.name}`}
              data-testid={`variation-row-${item.id}-archive`}
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(item, "archive");
              }}
              className="inline-flex items-center justify-center w-8 h-8 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <Archive className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <TrackingTable
      columns={columns}
      items={items}
      isLoading={isLoading}
      error={error}
      emptyMessage={emptyMessage}
      rowKey={(item) => item.id}
      onRowClick={onRowClick}
      highlightRowId={highlightRowId}
      expandedRowId={expandedRowId}
      renderExpandedRow={renderExpandedRow}
    />
  );
}
